/*
 * Description:
 * ---------------------
 * Bonding Gain panel — visualises WAN link aggregation ("bonding") in real
 * time: aggregate tunnel throughput, how that throughput is split across the
 * WANs, the live bonding-gain multiplier (aggregate ÷ busiest single link),
 * and the theoretical bonded-capacity ceiling (Σ per-WAN measured bandwidth).
 *
 * HOW BONDING SPLITS TRAFFIC
 *   NABx has ONE bonding action, with two tiers the data plane picks between
 *   per flow. Every flow is hashed into a schedule ring whose slots are handed
 *   out in proportion to each WAN's MEASURED CAPACITY, then pinned there for
 *   its lifetime — so links aggregate without anything being reordered, and a
 *   link a tenth the speed of its partner receives about a tenth of the flows
 *   instead of half of them. A flow that proves itself a sustained bulk
 *   transfer is additionally split packet-by-packet across the same weighted
 *   ring, so a single transfer can exceed one link; that tier only engages
 *   while the bonded links are close enough in delay for the receiver to
 *   re-sequence. So the gain multiplier rises with concurrent flows always,
 *   and on a single large transfer when the links are well matched.
 *
 * DATA SOURCE / ACCURACY
 *   All numbers derive from the GET /health snapshot the console already polls.
 *   Per-WAN tx/rx bytes/s come from kernel /sys/class/net/<wan>/statistics
 *   (authoritative, atomic), so they include the tunnel's UDP/IP encap overhead
 *   (~3–5% at MTU 1400) and a small always-on keepalive/probe baseline on every
 *   WAN. Consequently the gain multiplier reads ~1.0x at idle and is only
 *   meaningful under concurrent load. The capacity ceiling uses each WAN's
 *   measured burst bandwidth and requires throughput probing to be enabled.
 *
 * ! file BondingPanel.js
 * ---------------
 * \author: Tolga O. Atalay
 * \Affiliation: A2 Labs LLC / A12XT Inc.
 * \date: 2025
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { useWanName } from "./WanNames";

// ── Formatting ───────────────────────────────────────────────────────────────

// bytes/s → Mbps
const toMbps = (bytesPerSec) =>
  typeof bytesPerSec === "number" && isFinite(bytesPerSec)
    ? (bytesPerSec * 8) / 1e6
    : 0;

const fmtRate = (mbps) => {
  if (mbps == null || !isFinite(mbps)) return "—";
  if (mbps >= 1000) return `${(mbps / 1000).toFixed(2)} Gbps`;
  if (mbps >= 100) return `${mbps.toFixed(0)} Mbps`;
  if (mbps >= 10) return `${mbps.toFixed(1)} Mbps`;
  if (mbps >= 0.05) return `${mbps.toFixed(2)} Mbps`;
  return "0 Mbps";
};

const fmtGain = (g) => (g != null && isFinite(g) ? `${g.toFixed(2)}×` : "—");

// Per-WAN measured capacity (Mbps): prefer the download burst, fall back to
// the upload burst. null until a burst sample has arrived.
const measuredCap = (w) => {
  if (!w) return null;
  if (typeof w.measured_download_mbps === "number") return w.measured_download_mbps;
  if (typeof w.measured_bw_mbps === "number") return w.measured_bw_mbps;
  return null;
};

// A WAN is "carrying data" once its combined throughput clears a small floor
// that filters out idle keepalive/probe traffic (~0.1 Mbps).
const CARRY_FLOOR_MBPS = 0.1;

// Distinct colours for the per-WAN share bar (cycled if >6 WANs).
const WAN_COLORS = ["#2563eb", "#16a34a", "#d97706", "#9333ea", "#0891b2", "#dc2626"];

// Category traffic classes exposed by the SPREAD control. These mirror the
// classifier category set (BE is the catch-all every unmatched flow lands in).
const CATEGORY_CLASSES = ["BE", "CRITICAL", "REALTIME", "INTERACTIVE", "STREAMING", "BULK", "BACKGROUND", "DIAG"];

// Friendly label for an app class key ("APP_YOUTUBE" → "YouTube"-ish). Used as
// a fallback when /classes doesn't carry a label.
const friendlyApp = (key) =>
  String(key || "")
    .replace(/^APP_/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (ch) => ch.toUpperCase());

// Bulk-action button style for the "Set all" row. `active` fills the button to
// show every category is already in that mode. kind ∈ bond|failover.
const bulkBtnStyle = (kind, active) => {
  const fill = kind === "bond" ? "#16a34a" : "#475569";
  const txt = kind === "bond" ? "#166534" : "#475569";
  return {
    padding: "6px 12px", borderRadius: 8,
    border: `1px solid ${active ? fill : "#cbd5e1"}`,
    background: active ? fill : "#fff",
    color: active ? "#fff" : txt,
    fontSize: "0.75rem", fontWeight: 700, cursor: "pointer",
    boxShadow: active ? "0 1px 2px rgba(0,0,0,0.12)" : "none",
  };
};

// The two per-class scheduling modes. There used to be three ("spread" and
// "stripe" were separate, mutually exclusive bonding modes the operator had to
// choose between); they are now the two tiers of the single BOND action and the
// data plane picks between them per flow, so there is nothing left to choose.
const MODES = [
  { key: "failover", label: "Failover", color: "#475569", bg: "#f1f5f9", border: "#94a3b8" },
  { key: "bond", label: "Bond", color: "#166534", bg: "#f0fdf4", border: "#16a34a" },
];

// ── Sparkline (self-contained; fed from the local /health history buffer) ────

function Spark({ points, color = "#2563eb", height = 40, width = 260, fmt }) {
  const valid = (points || []).filter((p) => p.value != null && isFinite(p.value));
  if (valid.length < 2) {
    return (
      <div style={{
        height, width: "100%", display: "flex", alignItems: "center",
        justifyContent: "center", color: "#cbd5e1", fontSize: "0.72rem",
        fontStyle: "italic",
      }}>
        building trend…
      </div>
    );
  }
  const xs = valid.map((p) => p.ts);
  const ys = valid.map((p) => p.value);
  const xMin = Math.min(...xs);
  const xMax = Math.max(...xs);
  const yMax = Math.max(...ys, 0.0001) * 1.15 || 1;
  const sx = (x) => 2 + ((x - xMin) / Math.max(1, xMax - xMin)) * (width - 4);
  const sy = (y) => height - 2 - (y / Math.max(0.0001, yMax)) * (height - 4);
  const d = valid.map((p, i) => `${i === 0 ? "M" : "L"}${sx(p.ts).toFixed(1)} ${sy(p.value).toFixed(1)}`).join(" ");
  const fill = `${d} L${sx(xMax).toFixed(1)} ${(height - 2).toFixed(1)} L${sx(xMin).toFixed(1)} ${(height - 2).toFixed(1)} Z`;
  const last = valid[valid.length - 1].value;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" style={{ display: "block" }}>
        <path d={fill} fill={color} fillOpacity={0.1} stroke="none" />
        <path d={d} fill="none" stroke={color} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />
      </svg>
      <span style={{ fontSize: "0.72rem", fontWeight: 700, color }}>
        {fmt ? fmt(last) : last.toFixed(1)}
      </span>
    </div>
  );
}

// ── Summary stat card ────────────────────────────────────────────────────────

function StatCard({ label, value, sub, accent = "#0f172a", title }) {
  return (
    <div title={title} style={{
      flex: "1 1 190px", minWidth: 170, background: "#fff", border: "1px solid #e2e8f0",
      borderRadius: 12, padding: "16px 18px", display: "flex", flexDirection: "column", gap: 4,
    }}>
      <span style={{
        fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.08em",
        textTransform: "uppercase", color: "#64748b",
      }}>{label}</span>
      <span style={{ fontSize: "1.7rem", fontWeight: 800, color: accent, lineHeight: 1.1 }}>{value}</span>
      {sub && <span style={{ fontSize: "0.74rem", color: "#94a3b8", fontWeight: 500 }}>{sub}</span>}
    </div>
  );
}

// ── Main panel ───────────────────────────────────────────────────────────────

export default function BondingPanel({ nodeApiUrl, selectedNode }) {
  const wanName = useWanName();
  const [wans, setWans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastFetch, setLastFetch] = useState(null);

  // Aggregate history for the trend chart: { ts, value } of aggregate Mbps and
  // gain multiplier. ~120 samples ≈ 6 min at the 3 s poll cadence.
  const HISTORY_CAP = 120;
  const [aggHistory, setAggHistory] = useState([]);
  const [gainHistory, setGainHistory] = useState([]);

  // Overhead-free routed-traffic split from GET /stats/wan (BPF counter_map).
  // We keep the previous cumulative sample in a ref and delta it between polls
  // to derive per-WAN routed bytes/s (no encap/probe inflation).
  const prevWanCountersRef = useRef(null);
  const [routedRows, setRoutedRows] = useState([]);

  // Per-class scheduling action (failover|spread) from GET /policy, the
  // in-flight class while a toggle PUT applies, and the last toggle error.
  const [actions, setActions] = useState({}); // className -> "failover" | "bond"
  const [actionBusy, setActionBusy] = useState(null);
  const [actionErr, setActionErr] = useState("");

  // App classes grouped by parent category (from GET /classes) so each category
  // row can expand into its per-app spread/failover toggles. Empty on legacy
  // edges that don't serve /classes — the category toggles still work.
  const [appsByCat, setAppsByCat] = useState({}); // category -> [{ key, label }]
  const [expandedCats, setExpandedCats] = useState(() => new Set());

  useEffect(() => {
    if (!selectedNode) { setWans([]); setLoading(false); setAggHistory([]); setGainHistory([]); return; }

    const controller = new AbortController();
    setWans([]);
    setAggHistory([]);
    setGainHistory([]);
    setRoutedRows([]);
    prevWanCountersRef.current = null;
    setLoading(true);

    const doFetch = async () => {
      const url = nodeApiUrl("/health");
      if (!url) return;
      try {
        const res = await fetchWithTimeout(url, { signal: controller.signal });
        if (!res.ok) throw new Error(`${res.status}`);
        const data = await res.json();
        if (controller.signal.aborted) return;
        const list = Array.isArray(data?.health) ? data.health : [];
        setWans(list);
        setError("");
        setLastFetch(new Date());

        // Compute aggregate + gain and push to the trend buffers.
        let agg = 0;
        let best = 0;
        for (const w of list) {
          const t = toMbps(w.tx_bytes_per_sec) + toMbps(w.rx_bytes_per_sec);
          agg += t;
          if (t > best) best = t;
        }
        const gain = best > 0 ? agg / best : 1;
        const now = Date.now();
        setAggHistory((prev) => {
          const next = prev.concat({ ts: now, value: agg });
          if (next.length > HISTORY_CAP) next.splice(0, next.length - HISTORY_CAP);
          return next;
        });
        setGainHistory((prev) => {
          const next = prev.concat({ ts: now, value: gain });
          if (next.length > HISTORY_CAP) next.splice(0, next.length - HISTORY_CAP);
          return next;
        });
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("[BondingPanel] fetch error:", err);
        if (!controller.signal.aborted) setError(err.message || "Failed to load bonding data");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    // Poll the overhead-free per-WAN data-plane counters and delta them into a
    // routed-traffic split. Cumulative counters → bytes/s between polls.
    const doFetchWanStats = async () => {
      const url = nodeApiUrl("/stats/wan");
      if (!url) return;
      try {
        const res = await fetchWithTimeout(url, { signal: controller.signal });
        if (!res.ok) return;
        const data = await res.json();
        if (controller.signal.aborted) return;
        const list = Array.isArray(data?.wans) ? data.wans : [];
        const now = Date.now();
        const cur = {};
        for (const c of list) {
          if (c?.name) cur[c.name] = { bytes: Number(c.bytes) || 0, ts: now };
        }
        const prev = prevWanCountersRef.current;
        prevWanCountersRef.current = { at: now, byWan: cur };

        if (prev && prev.byWan) {
          const rows = [];
          for (const name of Object.keys(cur)) {
            const p = prev.byWan[name];
            if (!p) continue;
            const dt = (now - prev.at) / 1000;
            if (dt <= 0) continue;
            const db = cur[name].bytes - p.bytes;
            const mbps = db > 0 ? (db * 8) / (dt * 1e6) : 0;
            rows.push({ name, mbps });
          }
          rows.sort((a, b) => b.mbps - a.mbps);
          setRoutedRows(rows);
        }
      } catch (err) {
        // Non-critical: legacy edges without /stats/wan just omit this section.
        if (err.name !== "AbortError") { /* ignore */ }
      }
    };

    doFetch();
    doFetchWanStats();
    const timer = setInterval(() => { doFetch(); doFetchWanStats(); }, 3000);
    return () => { controller.abort(); clearInterval(timer); };
  }, [selectedNode, nodeApiUrl]);

  // Poll the per-class scheduling action so the SPREAD toggles reflect live
  // control-plane state (and pick up changes made from anywhere else).
  useEffect(() => {
    if (!selectedNode) { setActions({}); return; }
    const controller = new AbortController();
    const load = async () => {
      const url = nodeApiUrl("/policy/action");
      if (!url) return;
      try {
        const res = await fetchWithTimeout(url, { signal: controller.signal });
        if (!res.ok) return;
        const data = await res.json();
        if (controller.signal.aborted) return;
        // /policy/action reports the LIVE effective action for every class
        // (dynamic default = failover on a single WAN, spread with >=2 live
        // WANs) plus any per-class override, so the toggles reflect real
        // data-plane state and survive a page refresh. (GET /policy only
        // carries rules for an explicit policy file, so it is empty in
        // auto-policy mode and must not be used to derive the action.)
        setActions(data?.actions || {});
      } catch (err) { /* non-critical: legacy edges omit the action field */ }
    };
    load();
    const timer = setInterval(load, 5000);
    return () => { controller.abort(); clearInterval(timer); };
  }, [selectedNode, nodeApiUrl]);

  // Fetch the known traffic classes once per node and group the app-level ones
  // under their parent category, so each category row can expand into per-app
  // spread/failover toggles. Best-effort: legacy edges without /classes just
  // omit the expanders and keep the 8 category toggles.
  useEffect(() => {
    if (!selectedNode) { setAppsByCat({}); return; }
    const controller = new AbortController();
    (async () => {
      const url = nodeApiUrl("/classes");
      if (!url) return;
      try {
        const res = await fetchWithTimeout(url, { signal: controller.signal });
        if (!res.ok) return;
        const data = await res.json();
        if (controller.signal.aborted) return;
        const byCat = {};
        for (const c of (data?.classes || [])) {
          if (!c || !c.is_app || !c.category) continue;
          (byCat[c.category] ||= []).push({ key: c.key, label: c.label || friendlyApp(c.key) });
        }
        for (const cat of Object.keys(byCat)) {
          byCat[cat].sort((a, b) => a.label.localeCompare(b.label));
        }
        setAppsByCat(byCat);
      } catch (err) { /* non-critical: legacy edges omit /classes */ }
    })();
    return () => { controller.abort(); };
  }, [selectedNode, nodeApiUrl]);

  // Set the scheduling action (failover|spread|stripe) for one traffic class
  // via PUT /policy/action.
  const setClassAction = async (className, action) => {
    setActionBusy(className);
    setActionErr("");
    try {
      const url = nodeApiUrl("/policy/action");
      if (!url) return;
      const res = await fetchWithTimeout(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ class: className, action }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      setActions((prev) => ({ ...prev, [className]: action }));
    } catch (err) {
      setActionErr(`Failed to update ${className}: ${err.message || err}`);
    } finally {
      setActionBusy(null);
    }
  };

  // Apply one mode (bond|failover) to every category class — and bring
  // any per-app override into line with the same mode, so the bulk action is
  // fully reflected (no lingering app exception left behind).
  const setAllActions = async (action) => {
    const target = action;
    const appKeys = [];
    for (const c of CATEGORY_CLASSES) {
      for (const a of appsByCat[c] || []) {
        if (a.key in actions && actions[a.key] !== target) appKeys.push(a.key);
      }
    }
    const keys = [...CATEGORY_CLASSES, ...appKeys];
    // Optimistically flip everything at once for instant feedback.
    setActions((prev) => {
      const next = { ...prev };
      for (const k of keys) next[k] = target;
      return next;
    });
    setActionBusy("__all__");
    setActionErr("");
    try {
      const url = nodeApiUrl("/policy/action");
      if (!url) return;
      for (const k of keys) {
        // eslint-disable-next-line no-await-in-loop
        const res = await fetchWithTimeout(url, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ class: k, action: target }),
        });
        if (!res.ok) throw new Error(`${k}: ${res.status}`);
      }
    } catch (err) {
      setActionErr(`Failed to set all classes: ${err.message || err}`);
    } finally {
      setActionBusy(null);
    }
  };

  const toggleExpanded = (cat) =>
    setExpandedCats((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });

  // Current action for a class (dynamic default = failover until the control
  // plane reports otherwise, so an unloaded class never falsely shows bonding).
  const actionOf = (classKey) => actions[classKey] ?? "failover";

  // Render a 3-way Failover/Spread/Stripe segmented control for one class.
  const renderActionChip = (classKey, label, { small = false } = {}) => {
    const cur = actionOf(classKey);
    const busy = actionBusy === classKey;
    return (
      <div key={classKey} style={{ display: "flex", alignItems: "center", gap: 8, opacity: busy ? 0.6 : 1 }}>
        <span style={{ fontSize: small ? "0.74rem" : "0.8rem", fontWeight: 600, color: "#334155", minWidth: small ? 96 : 108 }}>{label}</span>
        <div style={{ display: "inline-flex", border: "1px solid #cbd5e1", borderRadius: 8, overflow: "hidden" }}>
          {MODES.map((m, i) => {
            const on = cur === m.key;
            return (
              <button
                key={m.key}
                onClick={() => { if (!on) setClassAction(classKey, m.key); }}
                disabled={busy || on}
                title={`Set ${label} to ${m.label}`}
                style={{
                  padding: small ? "5px 9px" : "6px 11px",
                  border: "none", borderLeft: i === 0 ? "none" : "1px solid #e2e8f0",
                  background: on ? m.bg : "#fff",
                  color: on ? m.color : "#94a3b8",
                  fontSize: small ? "0.7rem" : "0.74rem", fontWeight: 700,
                  cursor: busy || on ? "default" : "pointer",
                }}
              >
                {m.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Derived metrics ──
  const metrics = useMemo(() => {
    const rows = (wans || []).map((w) => {
      const up = toMbps(w.tx_bytes_per_sec);
      const down = toMbps(w.rx_bytes_per_sec);
      return {
        name: w.name,
        index: w.index,
        up,
        down,
        total: up + down,
        cap: measuredCap(w),
        score: typeof w.score === "number" ? w.score : null,
        degraded: !!w.degraded,
      };
    });
    rows.sort((a, b) => b.total - a.total);

    const aggregate = rows.reduce((s, r) => s + r.total, 0);
    const aggUp = rows.reduce((s, r) => s + r.up, 0);
    const aggDown = rows.reduce((s, r) => s + r.down, 0);
    const best = rows.length ? rows[0].total : 0;
    const gain = best > 0 ? aggregate / best : 1;
    const carrying = rows.filter((r) => r.total >= CARRY_FLOOR_MBPS).length;
    const capSamples = rows.map((r) => r.cap).filter((c) => c != null);
    const capCeiling = capSamples.length ? capSamples.reduce((s, c) => s + c, 0) : null;
    const capMeasured = capSamples.length;

    return { rows, aggregate, aggUp, aggDown, best, gain, carrying, capCeiling, capMeasured };
  }, [wans]);

  if (!selectedNode) {
    return <div style={{ padding: 24, color: "#64748b" }}>Select an edge to view bonding.</div>;
  }
  if (loading && wans.length === 0) {
    return <div style={{ padding: 24, color: "#64748b" }}>Loading bonding metrics…</div>;
  }
  if (error && wans.length === 0) {
    return <div style={{ padding: 24, color: "#dc2626" }}>Failed to load: {error}</div>;
  }

  const { rows, aggregate, aggUp, aggDown, best, gain, carrying, capCeiling, capMeasured } = metrics;

  // Gain accent: green once we're materially above a single link.
  const gainAccent = gain >= 1.5 ? "#16a34a" : gain >= 1.15 ? "#65a30d" : "#334155";

  // Aggregate category state for the bulk-action buttons: highlight a "Set all"
  // button only when every category is already in that mode (none highlighted =
  // mixed).
  const allBond = CATEGORY_CLASSES.every((c) => actionOf(c) === "bond");
  const allFailover = CATEGORY_CLASSES.every((c) => actionOf(c) === "failover");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {/* Summary cards */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <StatCard
          label="Aggregate throughput"
          value={fmtRate(aggregate)}
          sub={`↑ ${fmtRate(aggUp)}   ↓ ${fmtRate(aggDown)}`}
          accent="#0f172a"
          title="Sum of live per-WAN throughput across the tunnel (from /health kernel ifstats). Includes ~3–5% tunnel encap overhead."
        />
        <StatCard
          label="Bonding gain"
          value={fmtGain(gain)}
          sub={`vs. busiest link (${fmtRate(best)})`}
          accent={gainAccent}
          title="Aggregate ÷ busiest single WAN. >1.0× means flows are spread across links. Per-flow bonding needs many concurrent flows to exceed 1.0×; reads ~1.0× at idle."
        />
        <StatCard
          label="Links carrying data"
          value={`${carrying} / ${rows.length}`}
          sub={`above ${CARRY_FLOOR_MBPS} Mbps`}
          accent="#0369a1"
          title="Number of WANs currently carrying application traffic (filters out idle keepalive/probe baseline)."
        />
        <StatCard
          label="Bonded capacity ceiling"
          value={capCeiling != null ? fmtRate(capCeiling) : "—"}
          sub={
            capMeasured
              ? `Σ of ${capMeasured} measured link${capMeasured > 1 ? "s" : ""}`
              : "enable throughput probing"
          }
          accent="#7c3aed"
          title="Theoretical max = sum of each WAN's measured burst bandwidth. Requires the throughput probing tier (WAN Health panel)."
        />
      </div>

      {/* Bonding mode — per-class control */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
          <div style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#64748b" }}>
            Bonding mode
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: "0.72rem", color: "#94a3b8", fontWeight: 600 }}>Set all:</span>
            <button onClick={() => setAllActions("bond")} disabled={!!actionBusy} title="Bond every category across all admitted WANs" style={bulkBtnStyle("bond", allBond)}>Bond all</button>
            <button onClick={() => setAllActions("failover")} disabled={!!actionBusy} title="Fail over every category" style={bulkBtnStyle("failover", allFailover)}>Failover all</button>
          </div>
        </div>
        <div style={{ fontSize: "0.78rem", color: "#64748b", marginBottom: 12 }}>
          <strong>BOND</strong> aggregates every admitted WAN, splitting traffic in proportion to each link's{" "}
          <em>measured capacity</em> rather than evenly — so a fast link carries proportionally more and a slow one
          can't cap the total. Each flow is pinned to a single link (nothing is reordered), and a flow that proves
          itself a sustained bulk transfer is additionally split <em>packet by packet</em> across the same weighted
          schedule, so one transfer can exceed a single link. That second tier only engages while the bonded links are
          close enough in delay for the receiver to re-sequence; on a mismatched pair (fibre + cellular) it stays off
          and bonding still aggregates across many flows. <strong>FAILOVER</strong> keeps a class on one WAN at a time
          and sheds a degraded link. Changes apply live to the data plane — no policy file required. The default is
          dynamic: a class <strong>fails over</strong> while only one WAN is live and <strong>bonds</strong> once two or
          more are up. A QoS quality ceiling narrows a class's bond to the links that pass it. Expand a category to
          override individual apps.
        </div>
        {actionErr && (
          <div style={{ color: "#dc2626", fontSize: "0.8rem", marginBottom: 8 }}>{actionErr}</div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {CATEGORY_CLASSES.map((c) => {
            const apps = appsByCat[c] || [];
            const hasApps = apps.length > 0;
            const open = expandedCats.has(c);
            // Count apps whose action differs from their category (a true per-app override).
            const catAction = actionOf(c);
            const overridden = apps.filter((a) => a.key in actions && actionOf(a.key) !== catAction).length;
            return (
              <div key={c} style={{ border: "1px solid #eef2f7", borderRadius: 10, overflow: "hidden" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 8, background: "#fbfdff" }}>
                  {renderActionChip(c, c)}
                  {hasApps && (
                    <button
                      onClick={() => toggleExpanded(c)}
                      title={open ? `Hide ${c} apps` : `Show ${apps.length} ${c} apps`}
                      style={{
                        display: "flex", alignItems: "center", gap: 6, marginLeft: "auto",
                        padding: "6px 10px", borderRadius: 8, border: "1px solid #e2e8f0",
                        background: "#fff", color: "#475569", fontSize: "0.72rem", fontWeight: 600, cursor: "pointer",
                      }}
                    >
                      {overridden > 0 && (
                        <span style={{
                          fontSize: "0.64rem", fontWeight: 700, color: "#b45309",
                          background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "1px 6px",
                        }}>
                          {overridden} custom
                        </span>
                      )}
                      {apps.length} app{apps.length === 1 ? "" : "s"}
                      <span style={{ fontSize: "0.7rem" }}>{open ? "▲" : "▼"}</span>
                    </button>
                  )}
                </div>
                {hasApps && open && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "6px 10px 10px 10px", background: "#fff" }}>
                    {apps.map((a) => renderActionChip(a.key, a.label, { small: true }))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ fontSize: "0.78rem", color: "#64748b" }}>
        Whether the bond does no harm on this edge, against each link held alone, is measured by the
        bonding suite under Verification, Test Suites.
      </div>

      {/* Trend charts */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <div style={{
          flex: "2 1 320px", minWidth: 280, background: "#fff",
          border: "1px solid #e2e8f0", borderRadius: 12, padding: 16,
        }}>
          <div style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#64748b", marginBottom: 8 }}>
            Aggregate throughput (trend)
          </div>
          <Spark points={aggHistory} color="#2563eb" fmt={fmtRate} />
        </div>
        <div style={{
          flex: "1 1 220px", minWidth: 200, background: "#fff",
          border: "1px solid #e2e8f0", borderRadius: 12, padding: 16,
        }}>
          <div style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#64748b", marginBottom: 8 }}>
            Bonding gain (trend)
          </div>
          <Spark points={gainHistory} color={gainAccent} fmt={fmtGain} />
        </div>
      </div>

      {/* Per-WAN split bar */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16 }}>
        <div style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#64748b", marginBottom: 12 }}>
          Traffic distribution across links
        </div>
        {aggregate < CARRY_FLOOR_MBPS ? (
          <div style={{ color: "#94a3b8", fontSize: "0.82rem", fontStyle: "italic", padding: "8px 0" }}>
            No live traffic on the tunnel. Generate concurrent flows to see the split
            (see cloud-anchor/tools/nabx-loadgen.sh).
          </div>
        ) : (
          <>
            {/* Stacked share bar */}
            <div style={{ display: "flex", height: 26, borderRadius: 6, overflow: "hidden", border: "1px solid #e2e8f0", marginBottom: 14 }}>
              {rows.map((r, i) => {
                const share = aggregate > 0 ? (r.total / aggregate) * 100 : 0;
                if (share <= 0) return null;
                return (
                  <div
                    key={r.name}
                    title={`${wanName(r.name)}: ${fmtRate(r.total)} (${share.toFixed(1)}%)`}
                    style={{
                      width: `${share}%`, background: WAN_COLORS[i % WAN_COLORS.length],
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "#fff", fontSize: "0.7rem", fontWeight: 700, whiteSpace: "nowrap",
                    }}
                  >
                    {share >= 9 ? `${share.toFixed(0)}%` : ""}
                  </div>
                );
              })}
            </div>

            {/* Per-WAN rows */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {rows.map((r, i) => {
                const share = aggregate > 0 ? (r.total / aggregate) * 100 : 0;
                return (
                  <div key={r.name} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 3, background: WAN_COLORS[i % WAN_COLORS.length], flexShrink: 0 }} />
                    <span style={{ minWidth: 90, fontWeight: 700, color: "#0f172a", fontSize: "0.82rem" }}>{wanName(r.name)}</span>
                    {r.degraded && (
                      <span style={{ fontSize: "0.62rem", fontWeight: 800, color: "#a16207", background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 4, padding: "1px 5px" }}>DEGRADED</span>
                    )}
                    <div style={{ flex: 1, height: 8, borderRadius: 4, background: "#f1f5f9", overflow: "hidden" }}>
                      <div style={{ width: `${share}%`, height: "100%", background: WAN_COLORS[i % WAN_COLORS.length], transition: "width 0.5s ease" }} />
                    </div>
                    <span style={{ minWidth: 132, textAlign: "right", fontSize: "0.78rem", color: "#334155", fontWeight: 600 }}>
                      {fmtRate(r.total)}
                      <span style={{ color: "#94a3b8", fontWeight: 500 }}> · {share.toFixed(0)}%</span>
                    </span>
                    <span style={{ minWidth: 120, textAlign: "right", fontSize: "0.72rem", color: "#94a3b8" }}>
                      ↑{fmtRate(r.up)} ↓{fmtRate(r.down)}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Overhead-free routed-traffic split (BPF data-plane counters) */}
      {routedRows.length > 0 && (() => {
        const routedTotal = routedRows.reduce((s, r) => s + r.mbps, 0);
        return (
          <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16 }}>
            <div style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#64748b", marginBottom: 4 }}>
              Routed-traffic split · data-plane
            </div>
            <div style={{ fontSize: "0.72rem", color: "#94a3b8", marginBottom: 12 }}>
              Egress bytes the scheduler actually placed on each WAN, from the eBPF <code>counter_map</code> (<code>GET /stats/wan</code>). No tunnel encap or keepalive/probe inflation — upload direction only.
            </div>
            {routedTotal < 0.05 ? (
              <div style={{ color: "#94a3b8", fontSize: "0.82rem", fontStyle: "italic", padding: "8px 0" }}>
                No routed egress in the last interval.
              </div>
            ) : (
              <>
                <div style={{ display: "flex", height: 26, borderRadius: 6, overflow: "hidden", border: "1px solid #e2e8f0", marginBottom: 14 }}>
                  {routedRows.map((r, i) => {
                    const share = routedTotal > 0 ? (r.mbps / routedTotal) * 100 : 0;
                    if (share <= 0) return null;
                    return (
                      <div
                        key={r.name}
                        title={`${wanName(r.name)}: ${fmtRate(r.mbps)} (${share.toFixed(1)}%)`}
                        style={{
                          width: `${share}%`, background: WAN_COLORS[i % WAN_COLORS.length],
                          display: "flex", alignItems: "center", justifyContent: "center",
                          color: "#fff", fontSize: "0.7rem", fontWeight: 700, whiteSpace: "nowrap",
                        }}
                      >
                        {share >= 9 ? `${share.toFixed(0)}%` : ""}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {routedRows.map((r, i) => {
                    const share = routedTotal > 0 ? (r.mbps / routedTotal) * 100 : 0;
                    return (
                      <div key={r.name} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <span style={{ width: 10, height: 10, borderRadius: 3, background: WAN_COLORS[i % WAN_COLORS.length], flexShrink: 0 }} />
                        <span style={{ minWidth: 90, fontWeight: 700, color: "#0f172a", fontSize: "0.82rem" }}>{wanName(r.name)}</span>
                        <div style={{ flex: 1, height: 8, borderRadius: 4, background: "#f1f5f9", overflow: "hidden" }}>
                          <div style={{ width: `${share}%`, height: "100%", background: WAN_COLORS[i % WAN_COLORS.length], transition: "width 0.5s ease" }} />
                        </div>
                        <span style={{ minWidth: 132, textAlign: "right", fontSize: "0.78rem", color: "#334155", fontWeight: 600 }}>
                          {fmtRate(r.mbps)}
                          <span style={{ color: "#94a3b8", fontWeight: 500 }}> · {share.toFixed(0)}%</span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        );
      })()}

      {/* Methodology / accuracy note */}
      <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 12, padding: "14px 16px", fontSize: "0.78rem", color: "#475569", lineHeight: 1.55 }}>
        <div style={{ fontWeight: 800, color: "#334155", marginBottom: 6 }}>How this is measured</div>
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          <li><strong>Per-flow bonding:</strong> each 5-tuple flow is hashed onto one live WAN and pinned there — a single download rides one link. Aggregate gain appears only with many concurrent flows.</li>
          <li><strong>Source:</strong> live per-WAN throughput from kernel <code>/sys/class/net/&lt;wan&gt;/statistics</code> via <code>GET /health</code>. Authoritative, but includes ~3–5% tunnel UDP/IP encap overhead and a small always-on keepalive/probe baseline on every link.</li>
          <li><strong>Routed-traffic split</strong> is the overhead-free ground truth from the eBPF <code>counter_map</code> (<code>GET /stats/wan</code>): exactly how the scheduler distributed egress flows, with no encap or probe traffic counted. Upload direction only.</li>
          <li><strong>Bonding gain</strong> = live aggregate ÷ busiest single WAN. It reads ≈1.0× at idle (and can drift slightly above from probe traffic); it is only meaningful under sustained multi-flow load.</li>
          <li><strong>Capacity ceiling</strong> = Σ per-WAN measured burst bandwidth — needs the throughput probing tier enabled on the WAN Health panel.</li>
        </ul>
      </div>

      {lastFetch && (
        <div style={{ fontSize: "0.7rem", color: "#94a3b8", textAlign: "right" }}>
          updated {lastFetch.toLocaleTimeString()}
        </div>
      )}
    </div>
  );
}
